import React, { useEffect, useCallback, useRef, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import ChatEmptyState from './components/ChatEmptyState';
import ChatMessage from './components/ChatMessage';
import ChatTypingIndicator from './components/ChatTypingIndicator';
import ChatMessageSkeleton from './components/ChatMessageSkeleton';
import DateSeparator from './components/DateSeparator';
import { isSameDay } from '../../utils/inquiryUtils';

/**
 * 채팅 메시지 목록과 AI 타이핑 인디케이터를 조립하여 렌더링하는 메인 뷰 컴포넌트.
 */
const ChatView = ({ messages, isAiTyping, isSessionLoading = false, currentUser, messagesEndRef, completedTypingIds, handleComplete }) => {
    
    const scrollContainerRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    // 하단 스크롤 이동 함수 (useCallback으로 최적화)
    const scrollToBottom = useCallback((behaviorStr) => {
        const behavior = typeof behaviorStr === 'string' ? behaviorStr : 'smooth';
        if (messagesEndRef && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: behavior,
                block: 'end'
            });
        }
        setShowScrollButton(false);
    }, [messagesEndRef]);

    useEffect(() => {
        scrollToBottom('smooth');
    }, [messages, isAiTyping, completedTypingIds, scrollToBottom]);

    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        
        // 스크롤이 하단에서 얼마나 떨어져 있는지 계산
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        
        // 하단에서 150px 이상 위로 올렸을 때 버튼 표시 (약 메시지 1~2개 높이)
        setShowScrollButton(distanceFromBottom > 150);
    };

    // 초기 세션 로딩 중일 때 스켈레톤 표시
    if (isSessionLoading && messages.length === 0) {
        return (
            <div className="flex-1 overflow-y-hidden pr-2 py-4">
                <ChatMessageSkeleton count={3} />
            </div>
        );
    }

    // 메시지가 없는 경우 환영 화면 표시
    if (messages.length === 0) {
        return <ChatEmptyState userName={currentUser.name} />;
    }

    return (
        <div className="relative flex-1 flex flex-col min-h-0">
            <div 
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto pr-2 scroll-smooth no-scrollbar"
            >
                <div className="min-h-full flex flex-col justify-end py-4 space-y-4">
                    {/* 메시지 목록 렌더링 */}
                    {messages.map((msg, index) => {
                        const isNewDay = index === 0 || !isSameDay(msg.timestamp, messages[index - 1]?.timestamp);
                        
                        return (
                            <React.Fragment key={msg.id}>
                                {isNewDay && <DateSeparator date={msg.timestamp} />}
                                <ChatMessage
                                    msg={msg}
                                    isLastAiMessage={msg.role === 'ai' && index === messages.length - 1}
                                    completedTypingIds={completedTypingIds}
                                    handleComplete={handleComplete}
                                    scrollToBottom={scrollToBottom}
                                />
                            </React.Fragment>
                        );
                    })}

                    {/* AI 답변 대기 중 인디케이터 */}
                    {isAiTyping && <ChatTypingIndicator />}

                    {/* 하단 스크롤 기준점 */}
                    <div ref={messagesEndRef} className="h-0.5" />
                </div>
            </div>

            {/* Scroll to Bottom 플로팅 버튼 */}
            <div 
                className={`absolute bottom-4 right-4 sm:bottom-6 sm:right-6 transition-all duration-300 pointer-events-none
                    ${showScrollButton ? 'opacity-100 translate-y-0 scale-100 z-10' : 'opacity-0 translate-y-4 scale-90 -z-10'}`}
            >
                <button
                    onClick={() => scrollToBottom('smooth')}
                    className="p-3 bg-white text-gray-500 hover:text-blue-600 border border-gray-200 hover:border-blue-200 rounded-full shadow-lg hover:shadow-xl transition-all pointer-events-auto"
                    title="최신 메시지 보기 (맨 아래로 스크롤)"
                >
                    <ArrowDown size={20} strokeWidth={2.5} />
                </button>
            </div>
        </div>
    );
};

export default ChatView;
